import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Grid, Header, Button, Icon, Modal, Dropdown, Message } from 'semantic-ui-react';
import ManagementHeader from '../../../common/components/ManagementHeader';
import { CharactersListEnh } from '../lists';
import { CharacterFormEnh } from '../forms';
import { useStableCharacterZone, useStableSharedData } from '../../hooks/useStableHooks';
import '../../styles/bookWorksheet.css';

/**
 * CharacterManagementEnh component
 * Enhanced version of CharacterManagement with improved UI matching CharacterManagement-bcw
 * Manages characters for a selected book
 * OPTIMIZED: Memoized to prevent unnecessary re-renders during text editing
 * @returns {JSX.Element} CharacterManagementEnh component
 */
const CharacterManagementEnh = () => {
  // 🚀 PERFORMANCE OPTIMIZATION: Add render tracking
  const renderCount = useRef(0);
  renderCount.current++;

  if (process.env.NODE_ENV === 'development' && window.DEBUG_RENDERS) {
    console.log(`[RENDER] CharacterManagement render #${renderCount.current}`);
  }

  // 🚀 NEW: Use stable zone-based hooks for optimal performance
  const characterManagement = useStableCharacterZone();
  const sharedData = useStableSharedData();

  // Extract data from stable hooks (different interface than composite hooks)
  const {
    selectedCharacter,
    actions: characterActions
  } = characterManagement;

  const {
    litGroups,
    books,
    selections,
    actions: sharedActions
  } = sharedData;

  // Map to old interface for compatibility with safety checks
  const litGroupsArray = Array.isArray(litGroups) ? litGroups : [];
  const booksArray = useMemo(() => Array.isArray(books) ? books : [], [books]);
  const selectionsObj = selections || {};

  const selectedBook = booksArray.find(b => b.uuid === selectionsObj.bookId) || null;
  const litGroupId = selectionsObj.litGroupId;
  const bookId = selectionsObj.bookId;
  const characterId = selectionsObj.characterId;

  // Memoized actions to prevent dependency issues
  const setLitGroupSelection = useMemo(() => sharedActions.setLitGroupSelection, [sharedActions.setLitGroupSelection]);
  const setBookSelection = useMemo(() => sharedActions.setBookSelection, [sharedActions.setBookSelection]);
  const setCharacterSelection = useMemo(() => characterActions.selectCharacter, [characterActions.selectCharacter]);
  const clearCharacterSelection = useCallback(() => characterActions.selectCharacter(null), [characterActions]);
  const fetchLitGroups = useMemo(() => sharedActions.fetchLitGroups, [sharedActions.fetchLitGroups]);
  const fetchBooks = useMemo(() => sharedActions.fetchBooks, [sharedActions.fetchBooks]);
  const fetchCharacters = useMemo(() => characterActions.fetchCharacters, [characterActions.fetchCharacters]);
  const fetchCharacter = useMemo(() => characterActions.fetchCharacter || (() => Promise.resolve()), [characterActions.fetchCharacter]);
  const fetchLitGroup = useCallback(() => Promise.resolve(), []); // Not needed in new architecture
  const fetchBook = useCallback(() => Promise.resolve(), []); // Not needed in new architecture
  const getBooksByLitGroup = useCallback((litGroupId) => booksArray.filter(book => book.litGroupId === litGroupId), [booksArray]);

  // Derived data computed from hook data
  const booksForLitGroup = litGroupId ? getBooksByLitGroup(litGroupId) : [];
  const litGroupOptions = litGroupsArray
    .filter((lg, index, self) => self.findIndex(l => l.uuid === lg.uuid) === index)
    .map(lg => ({
      key: lg.uuid,
      text: lg.name || 'Unnamed Literary Group',
      value: lg.uuid
    }));

  // Local state
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [characterToDelete, setCharacterToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Update editing state when character ID changes
  useEffect(() => {
    setIsEditing(!!characterId);
  }, [characterId]);

  // 🚀 NEW: Initialize data fetching on mount
  useEffect(() => {
    // Fetch initial data when component mounts
    fetchLitGroups();
  }, [fetchLitGroups]);

  // Auto-fetch books when litGroupId changes
  useEffect(() => {
    if (litGroupId) {
      fetchBooks(litGroupId);
    }
  }, [litGroupId, fetchBooks]);

  // Auto-fetch characters when bookId changes
  useEffect(() => {
    console.log('[CHARACTER_MGMT] useEffect triggered - bookId:', bookId, 'fetchCharacters:', typeof fetchCharacters);
    if (bookId) {
      console.log('[CHARACTER_MGMT] Calling fetchCharacters for bookId:', bookId);
      fetchCharacters(bookId);
    }
  }, [bookId, fetchCharacters]);

  // Conditional logging for performance
  if (process.env.NODE_ENV === 'development' && window.DEBUG_CHARACTER_MANAGEMENT) {
    console.log('LitGroup ID:', litGroupId);
    console.log('Books for LitGroup:', booksForLitGroup);
    console.log('Selected Book:', selectedBook);
  }

  // Create dropdown options from all available books (deduplicated by UUID)
  let bookOptions = booksForLitGroup
    .filter((book, index, self) => self.findIndex(b => b.uuid === book.uuid) === index)
    .map(book => ({
      key: book.uuid,
      text: book.name || 'Unnamed Book',
      value: book.uuid
    }));

  // If we have a selected book but it's not in the options, add it
  if (selectedBook && !bookOptions.some(option => option.value === selectedBook.uuid)) {
    bookOptions.unshift({
      key: selectedBook.uuid,
      text: selectedBook.name || 'Selected Book',
      value: selectedBook.uuid
    });
  }

  // Conditional logging for performance
  if (process.env.NODE_ENV === 'development' && window.DEBUG_CHARACTER_MANAGEMENT) {
    console.log('Book Options:', bookOptions);
  }

  // Handle new character button click
  const handleNewCharacterClick = useCallback(() => {
    clearCharacterSelection();
  }, [clearCharacterSelection]);

  // Handle delete character
  const handleDeleteCharacter = useCallback((character) => {
    setCharacterToDelete(character);
    setShowDeleteModal(true);
  }, []);

  // Handle confirm delete character
  const confirmDeleteCharacter = useCallback(async () => {
    if (!characterToDelete?.uuid) return;

    try {
      // Call the unified delete thunk with enhanced options
      await characterActions.deleteCharacter({
        characterId: characterToDelete.uuid,
        bookId,
        options: {
          cleanup: 'cleanup',    // Enable S3 cleanup
          cascade: 'cascade',    // Enable cascade deletion
          dryRun: false,         // Actually perform deletion
          force: false           // Use safety checks
        }
      });

      // Close the modal
      setShowDeleteModal(false);
      setCharacterToDelete(null);

      // If the deleted character was selected, clear the selection
      if (selectedCharacter && selectedCharacter.uuid === characterToDelete.uuid) {
        clearCharacterSelection();
      }

      // Refresh the characters list
      if (bookId) {
        fetchCharacters(bookId);
      }

      setSuccessMessage('Character deleted successfully with cascade cleanup');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting character via unified system:', error);
      setErrorMessage('Failed to delete character');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  }, [characterToDelete, characterActions, bookId, selectedCharacter, clearCharacterSelection, fetchCharacters]);

  // Handle lit group selection
  const handleLitGroupSelect = useCallback((value) => {
    setLitGroupSelection(value);
    clearCharacterSelection();

    // Fetch the literary group data
    fetchLitGroup(value);

    // Fetch books for this literary group
    fetchBooks(value).then(() => {
      // After fetching books, get the updated list of books for this LitGroup
      const updatedBooks = getBooksByLitGroup(value);
      console.log('Updated books after fetch:', updatedBooks);

      // If there are books, select the first one with a profile, or the first one if none have profiles
      if (updatedBooks && updatedBooks.length > 0) {
        // Find a book with a profile (not profileMissing)
        const bookWithProfile = updatedBooks.find(book => !book.profileMissing);

        if (bookWithProfile) {
          console.log('Selecting book with profile:', bookWithProfile);
          setBookSelection(bookWithProfile.uuid);
        } else {
          console.log('No books with profiles found, selecting first book:', updatedBooks[0]);
          setBookSelection(updatedBooks[0].uuid);
        }
      }
    });
  }, [setLitGroupSelection, clearCharacterSelection, fetchLitGroup, fetchBooks, getBooksByLitGroup, setBookSelection]);

  // Handle book selection
  const handleBookSelect = useCallback((value) => {
    setBookSelection(value);
    clearCharacterSelection();

    // Fetch the book data
    fetchBook(value);

    // Fetch characters for this book
    fetchCharacters(value);
  }, [setBookSelection, clearCharacterSelection, fetchBook, fetchCharacters]);

  // Handle character selection
  const handleCharacterSelect = useCallback((character) => {
    console.log('🎯 [CHARACTER_MGMT] handleCharacterSelect called with:', {
      character: character ? { uuid: character.uuid, name: character.name } : null,
      bookId,
      currentCharacterId: characterId
    });

    if (character && character.uuid) {
      console.log('🎯 [CHARACTER_MGMT] Setting character selection:', character.uuid);
      // Set the selection first
      setCharacterSelection(character.uuid);

      console.log('🎯 [CHARACTER_MGMT] Fetching character data for:', character.uuid);
      // Then fetch the character data using the zone-based hook method
      fetchCharacter(character.uuid, bookId)
        .then(() => {
          console.log('🎯 [CHARACTER_MGMT] Successfully fetched character data');
        })
        .catch(error => {
          console.error('🎯 [CHARACTER_MGMT] Error fetching character:', error);
        });
      setIsEditing(true);
      console.log('🎯 [CHARACTER_MGMT] Character selection complete');
    } else {
      console.warn('🎯 [CHARACTER_MGMT] Invalid character passed to handleCharacterSelect:', character);
    }
  }, [setCharacterSelection, fetchCharacter, bookId, characterId]);

  // Handle save
  const handleSaveCharacter = useCallback((updatedCharacter) => {
    // Refresh data after save
    if (updatedCharacter && updatedCharacter.uuid) {
      // Set the selection first
      setCharacterSelection(updatedCharacter.uuid);
      // Then fetch the character data using the zone-based hook method
      fetchCharacter(updatedCharacter.uuid, bookId);
      setIsEditing(true);

      // Refresh the characters list
      if (bookId) {
        fetchCharacters(bookId);
      }

      setSuccessMessage('Character saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  }, [setCharacterSelection, fetchCharacter, bookId, fetchCharacters]);

  return (
    <ManagementHeader
      title={<div><span>Character Management:  </span><span className="bw-subTitle">{selectedBook?.name || ''}</span></div>}
      error={errorMessage}
      isLoading={false}
      width="1024px"
      isEditing={isEditing}
    >
      <Grid>
        <Grid.Row>
          <Grid.Column width={6}>
            <div>
              <div style={{ marginBottom: '1em', display: 'none' }}>
                <div style={{
                  marginTop: '0.6em',
                  position: 'relative'
                }}>
                  <label style={{ display: 'block', marginBottom: '0.5em' }}>Select Literary Group</label>
                  <Dropdown
                    selection
                    options={litGroupOptions}
                    value={litGroupId}
                    onChange={(e, { value }) => handleLitGroupSelect(value)}
                    placeholder="Select Literary Group"
                    fluid
                    style={{ zIndex: 1000 }}
                    disabled={litGroupOptions.length === 0}
                  />
                </div>
              </div>

              {litGroupId && 1 === -0 && (
                <div style={{ marginBottom: '1em' }}>
                  <div style={{
                    marginTop: '0.6em',
                    position: 'relative'
                  }}>
                    <label style={{ display: 'block', marginBottom: '0.5em' }}>Select Book</label>
                    <Dropdown
                      selection
                      options={bookOptions}
                      value={bookId}
                      onChange={(e, { value }) => handleBookSelect(value)}
                      placeholder="Select Book"
                      fluid
                      style={{ zIndex: 999 }}
                      disabled={bookOptions.length === 0}
                    />
                    {bookOptions.length === 0 && litGroupId && (
                      <Message info size="tiny" style={{ marginTop: '0.5em' }}>
                        <p>No books found for this literary group. Please create a book first.</p>
                      </Message>
                    )}
                  </div>
                </div>
              )}

              {selectedBook && (
                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px'
                  }}>
                    <Header as="h3" style={{ margin: 0 }}>Characters</Header>
                    <Button
                      primary
                      size="small"
                      icon
                      labelPosition="left"
                      onClick={handleNewCharacterClick}
                      style={{ marginRight: '10px' }}
                    >
                      <Icon name="add" />
                      New Character
                    </Button>
                  </div>

                  <CharactersListEnh
                    onCharacterSelect={handleCharacterSelect}
                    onDeleteCharacter={handleDeleteCharacter}
                  />
                </div>
              )}
            </div>
          </Grid.Column>

          <Grid.Column width={10}>
            {selectedBook && (
              <>
                {successMessage && (
                  <Message positive>
                    <Message.Header>Success</Message.Header>
                    <p>{successMessage}</p>
                  </Message>
                )}
                <CharacterFormEnh onSave={handleSaveCharacter} />
              </>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* Delete Confirmation Modal */}
      <Modal open={showDeleteModal} size="small">
        <Modal.Header>Delete Character</Modal.Header>
        <Modal.Content>
          <p>Are you sure you want to delete this character? This action cannot be undone.</p>
          {characterToDelete && (
            <Message warning>
              <Message.Header>You are about to delete:</Message.Header>
              <p><strong>{characterToDelete.name}</strong></p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button negative onClick={confirmDeleteCharacter}>
            Delete
          </Button>
          <Button onClick={() => {
            setShowDeleteModal(false);
            setCharacterToDelete(null);
          }}>
            Cancel
          </Button>
        </Modal.Actions>
      </Modal>
    </ManagementHeader>
  );
};

// 🚀 PERFORMANCE OPTIMIZATION: Fix broken React.memo comparison
// Since CharacterManagement doesn't receive props, we can use a simple memo without comparison
// This will prevent re-renders unless the component's internal state or Redux subscriptions change
export default React.memo(CharacterManagementEnh);
